import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Monitor,
  Headphones,
  Shield,
  Wrench,
  Lock,
  Database,
  BarChart3,
  Laptop,
  ClipboardCheck,
  Settings,
  DollarSign,
  Clock,
  ShieldCheck,
  Users,
  TrendingUp,
  Target,
  Check,
  X,
  Cloud,
  Lightbulb,
  Server,
  Phone
} from "lucide-react";

const ManagedITPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetQuote = () => {
    navigate("/#contact");
  };

  const features = [
    { icon: Monitor, title: "24/7 Network Monitoring", description: "We continuously monitor your systems to detect and resolve issues before they impact your business", color: "#3B82F6" },
    { icon: Headphones, title: "Unlimited Help Desk Support", description: "Your team gets access to our expert technicians whenever they need assistance", color: "#2C5F2D" },
    { icon: Shield, title: "Regular System Updates & Patches", description: "We keep your software current and secure with automated updates and patches", color: "#3B82F6" },
    { icon: Wrench, title: "Proactive Maintenance", description: "Scheduled maintenance to prevent problems and optimize performance", color: "#2C5F2D" },
    { icon: Lock, title: "Security Management", description: "Comprehensive security measures to protect your data and systems", color: "#3B82F6" },
    { icon: Database, title: "Backup & Disaster Recovery", description: "Automated backups and recovery plans to protect your critical data", color: "#2C5F2D" },
    { icon: BarChart3, title: "Strategic IT Planning", description: "Ongoing guidance to align technology with your business goals", color: "#3B82F6" },
    { icon: Laptop, title: "Hardware & Software Management", description: "Full lifecycle management of your technology assets", color: "#2C5F2D" }
  ];

  const benefits = [
    { icon: DollarSign, title: "Predictable Costs", description: "No surprise bills. Know exactly what you'll spend on IT each month.", color: "#2C5F2D" },
    { icon: Clock, title: "Reduced Downtime", description: "Proactive monitoring catches issues before they cause disruptions.", color: "#3B82F6" },
    { icon: ShieldCheck, title: "Enhanced Security", description: "Enterprise-grade protection against cyber threats and data breaches.", color: "#2C5F2D" },
    { icon: Users, title: "Expert Team", description: "Access to experienced IT professionals without hiring full-time staff.", color: "#3B82F6" },
    { icon: TrendingUp, title: "Scalability", description: "Easily scale your IT support as your business grows.", color: "#2C5F2D" },
    { icon: Target, title: "Focus on Core Business", description: "Spend time on what matters—growing your business, not fixing IT issues.", color: "#3B82F6" }
  ];

  const rightForYou = [
    "Have 5-50 employees",
    "Rely on technology for daily operations",
    "Don't have dedicated IT staff",
    "Experience frequent technology issues",
    "Are concerned about cybersecurity",
    "Want to avoid unexpected IT expenses",
    "Need to comply with industry regulations",
    "Want to modernize their technology"
  ];

  const breakFixItems = [
    "Pay per incident (unpredictable costs)",
    "Reactive—fix problems after they occur",
    "Downtime affects your business",
    "No proactive monitoring",
    "Limited strategic guidance",
    "Hourly billing can add up quickly"
  ];

  const managedITItems = [
    "Fixed monthly fee (predictable budget)",
    "Proactive—prevent problems before they happen",
    "Minimize downtime with 24/7 monitoring",
    "Continuous system optimization",
    "Strategic IT planning and guidance",
    "Unlimited support included"
  ];

  const faqs = [
    { q: "What exactly is managed IT?", a: "Managed IT services provide comprehensive technology support on a subscription basis. Instead of calling a technician only when something breaks, we proactively monitor, maintain, and optimize your systems 24/7." },
    { q: "How is pricing determined?", a: "Pricing is based on factors like the number of users, devices, servers, and the level of support needed. We'll provide a custom quote after assessing your specific needs." },
    { q: "Do I need to sign a long-term contract?", a: "We offer flexible terms. While many clients prefer annual agreements for cost savings, we can discuss options that work for your business." },
    { q: "What happens if my system goes down?", a: "We respond immediately to critical issues. Our monitoring systems often detect and resolve problems before you even notice them. For urgent issues, you'll have direct access to our support team." },
    { q: "Can you work with my existing IT equipment?", a: "Absolutely. We work with your current technology and provide recommendations for upgrades only when necessary." },
    { q: "How quickly can you respond to issues?", a: "Response times vary by severity. Critical issues get immediate attention, while routine requests are typically addressed within a few hours." },
    { q: "Will I have a dedicated support person?", a: "You'll have access to our entire team of experts, ensuring someone is always available to help. For larger clients, we can assign a dedicated account manager." },
    { q: "What if I already have some IT staff?", a: "Perfect! We can work alongside your existing team, filling gaps and providing expertise in specialized areas. This is called co-managed IT." }
  ];

  return (
    <div className="min-h-screen" data-testid="managed-it-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/services" className="hover:text-blue-600 transition-colors">Services</Link>
              <span className="mx-2">›</span>
              <span>Managed IT Services</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              Managed IT Services
            </h1>
            <p className="text-lg" style={{ color: "#4A5568", maxWidth: "700px", margin: "0 auto" }}>
              Your complete IT department without the overhead
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-20 px-5 bg-white" data-testid="hero-section">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="flex justify-center">
                <Server size={180} style={{ color: "#3B82F6" }} className="hidden md:block" />
                <Server size={120} style={{ color: "#3B82F6" }} className="md:hidden" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: "#1F2937" }}>
                  Focus on Your Business, We'll Handle the Technology
                </h2>
                <p className="text-lg mb-8 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Our managed IT services provide comprehensive technology support, proactive monitoring, and strategic guidance—all for a predictable monthly fee. Focus on growing your business while we ensure your technology runs smoothly.
                </p>
                <Button
                  onClick={handleGetQuote}
                  data-testid="hero-cta"
                  className="text-lg font-semibold px-8 py-6 rounded-md"
                  style={{ backgroundColor: "#3B82F6" }}
                >
                  Get a Quote
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="included-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              What's Included in Our Managed IT Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  data-testid={`feature-card-${index}`}
                  className="bg-white border border-gray-200 rounded-xl p-8"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                >
                  <feature.icon size={40} style={{ color: feature.color }} className="mb-3" />
                  <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>{feature.title}</h3>
                  <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 px-5 bg-white" data-testid="how-it-works">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              How Our Managed IT Services Work
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="p-8 rounded-xl border-2 border-gray-200" style={{ backgroundColor: "#F9FAFB" }} data-testid="step-1">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold mb-5" style={{ backgroundColor: "#3B82F6" }}>1</div>
                <ClipboardCheck size={48} style={{ color: "#3B82F6" }} className="mb-4" />
                <h3 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Assessment</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>We evaluate your current IT infrastructure</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Identify vulnerabilities and opportunities</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Review your business goals and challenges</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Create a customized support plan</span></li>
                </ul>
              </div>
              {/* Step 2 */}
              <div className="p-8 rounded-xl border-2 border-gray-200" style={{ backgroundColor: "#F9FAFB" }} data-testid="step-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold mb-5" style={{ backgroundColor: "#2C5F2D" }}>2</div>
                <Settings size={48} style={{ color: "#2C5F2D" }} className="mb-4" />
                <h3 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Implementation</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3"><span style={{ color: "#2C5F2D" }}>•</span><span style={{ color: "#4A5568" }}>Deploy monitoring and management tools</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#2C5F2D" }}>•</span><span style={{ color: "#4A5568" }}>Set up help desk support systems</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#2C5F2D" }}>•</span><span style={{ color: "#4A5568" }}>Configure security and backup solutions</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#2C5F2D" }}>•</span><span style={{ color: "#4A5568" }}>Train your team on new processes</span></li>
                </ul>
              </div>
              {/* Step 3 */}
              <div className="p-8 rounded-xl border-2 border-gray-200" style={{ backgroundColor: "#F9FAFB" }} data-testid="step-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold mb-5" style={{ backgroundColor: "#3B82F6" }}>3</div>
                <Headphones size={48} style={{ color: "#3B82F6" }} className="mb-4" />
                <h3 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Ongoing Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>24/7 proactive monitoring</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Immediate issue resolution</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Regular system optimization</span></li>
                  <li className="flex items-start gap-3"><span style={{ color: "#3B82F6" }}>•</span><span style={{ color: "#4A5568" }}>Monthly performance reports</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="benefits-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              The Benefits of Managed IT Services
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  data-testid={`benefit-card-${index}`}
                  className="bg-white rounded-xl p-8 text-center"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                >
                  <benefit.icon size={48} style={{ color: benefit.color }} className="mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>{benefit.title}</h3>
                  <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Benefits Section */}
        <section className="py-20 px-5 bg-white" data-testid="who-benefits">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
              Is Managed IT Right for Your Business?
            </h2>
            <p className="text-lg text-center mb-10" style={{ color: "#4A5568" }}>
              Our services are perfect for businesses that:
            </p>
            <div className="rounded-xl p-8 md:p-12" style={{ backgroundColor: "#F9FAFB", maxWidth: "700px", margin: "0 auto" }}>
              {rightForYou.map((item, index) => (
                <div key={index} className="flex items-center gap-4 py-3">
                  <Check size={24} style={{ color: "#2C5F2D", flexShrink: 0 }} />
                  <span className="text-lg" style={{ color: "#374151" }}>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-lg italic text-center mt-8" style={{ color: "#4A5568" }}>
              If this sounds like your business, we should talk.
            </p>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="comparison-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Managed IT vs. Break-Fix Support
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Break-Fix */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#FCA5A5" }} data-testid="break-fix">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#1F2937" }}>Break-Fix Support</h3>
                {breakFixItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 py-2">
                    <X size={20} style={{ color: "#EF4444", flexShrink: 0, marginTop: "2px" }} />
                    <span style={{ color: "#4A5568" }}>{item}</span>
                  </div>
                ))}
              </div>
              {/* Managed IT */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#86EFAC" }} data-testid="managed-it-comparison">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#1F2937" }}>Managed IT Services</h3>
                {managedITItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 py-2">
                    <Check size={20} style={{ color: "#22C55E", flexShrink: 0, marginTop: "2px" }} />
                    <span style={{ color: "#4A5568" }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-5 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  data-testid={`faq-${index}`}
                  className="rounded-lg p-6"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>{faq.q}</h3>
                  <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-5" style={{ background: "linear-gradient(to right, #3B82F6, #2563EB)" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg mb-12" style={{ color: "rgba(255,255,255,0.9)" }}>
              Let's discuss how managed IT services can help your business
            </p>
            
            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ backgroundColor: "white", color: "#3B82F6" }}>1</div>
                <p className="text-white text-lg">Schedule a free consultation</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ backgroundColor: "white", color: "#3B82F6" }}>2</div>
                <p className="text-white text-lg">We'll assess your needs and provide a custom proposal</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ backgroundColor: "white", color: "#3B82F6" }}>3</div>
                <p className="text-white text-lg">Start enjoying worry-free IT support</p>
              </div>
            </div>

            <Button
              onClick={handleGetQuote}
              data-testid="cta-btn"
              className="text-lg font-bold px-10 py-6 rounded-lg hover:bg-gray-100 transition-all"
              style={{ backgroundColor: "white", color: "#3B82F6" }}
            >
              Schedule Free Assessment
            </Button>
            <p className="mt-5 text-white">
              Or call us:{" "}
              <a href="tel:7783497449" className="font-semibold hover:underline" data-testid="cta-phone">
                (778) 349-7449
              </a>
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="related-services">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12" style={{ color: "#1F2937" }}>
              You Might Also Be Interested In
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 text-center transition-all hover:-translate-y-1" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Shield size={40} style={{ color: "#2C5F2D" }} className="mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>Cybersecurity Services</h3>
                <p className="text-base mb-4" style={{ color: "#4A5568" }}>Protect your business from today's evolving threats</p>
                <Link to="/services/cybersecurity" className="font-semibold hover:underline" style={{ color: "#3B82F6" }}>Learn More →</Link>
              </div>
              <div className="bg-white rounded-xl p-8 text-center transition-all hover:-translate-y-1" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Cloud size={40} style={{ color: "#3B82F6" }} className="mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>Cloud Solutions</h3>
                <p className="text-base mb-4" style={{ color: "#4A5568" }}>Work from anywhere with secure cloud services</p>
                <Link to="/services/cloud-solutions" className="font-semibold hover:underline" style={{ color: "#3B82F6" }}>Learn More →</Link>
              </div>
              <div className="bg-white rounded-xl p-8 text-center transition-all hover:-translate-y-1" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Lightbulb size={40} style={{ color: "#2C5F2D" }} className="mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>IT Consulting</h3>
                <p className="text-base mb-4" style={{ color: "#4A5568" }}>Strategic technology guidance for your business</p>
                <Link to="/services/it-consulting" className="font-semibold hover:underline" style={{ color: "#3B82F6" }}>Learn More →</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ManagedITPage;
